/**
 * DemoSharePage — Iter-112 · Item 4. PUBLIC prospect view of a shared
 * Cleaners demo account. No login required. Expired/revoked links show a
 * clean message.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Sparkles, MapPin, AlertTriangle, Route as RouteIcon, ClipboardList, Clock3 } from 'lucide-react';
import { Card } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoSharePage() {
  const { token } = useParams();
  const [account, setAccount] = useState(null);
  const [err, setErr] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/cleaners/demo-accounts/shares/public/${token}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'This demo link is unavailable.');
        return d;
      })
      .then((d) => { setAccount(d.account); setExpiresAt(d.expires_at); })
      .catch((e) => setErr(e.message));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#101014' }}>
        <div className="max-w-md w-full rounded-2xl p-8 text-center" style={{ backgroundColor: '#1a1a1a', border: '2px solid #C9A962' }} data-testid="demo-share-expired">
          <Clock3 className="h-10 w-10 mx-auto mb-3" style={{ color: '#C9A962' }} />
          <h1 className="text-xl font-black text-white mb-2">Demo Link Unavailable</h1>
          <p className="text-sm text-zinc-400 leading-relaxed">{err}</p>
          <p className="text-xs text-zinc-500 mt-4">Noble Empire Cleaning Services · Mon–Fri 8:30 AM–4:30 PM CDT</p>
        </div>
      </div>
    );
  }
  if (!account) return <div className="min-h-screen" style={{ backgroundColor: '#101014' }} />;

  const accent = account.accent || '#22d3ee';
  const board = account.board || {};

  return (
    <div className="min-h-screen text-white pb-16" style={{ backgroundColor: '#101014' }} data-testid="demo-share-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 space-y-6">
        <div className="rounded-2xl p-6 border-2" style={{ borderColor: accent, background: 'linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 70%)' }} data-testid="demo-share-hero">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: accent }}>
            {account.necs_name} · Live Demo Experience · Noble Empire Cleaning Services
          </p>
          <h1 className="text-3xl font-black mt-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="demo-share-title">
            {account.property_name}
          </h1>
          <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" style={{ color: accent }} /> {account.address} · {account.label}
          </p>
          {expiresAt && (
            <p className="text-[10px] mt-2 text-zinc-500">
              This preview link expires {new Date(expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="demo-share-details">
          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>Property Details</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(account.details || {}).map(([k, v]) => (
              <div key={k} className="rounded px-3 py-2" style={{ backgroundColor: '#0f0f0f', border: `1px solid ${accent}55` }}>
                <p className="text-[9px] uppercase tracking-widest text-zinc-500">{k.replace(/_/g, ' ')}</p>
                <p className="text-sm font-black text-white">{String(v)}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="demo-share-packages">
          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>{account.necs_name} · Service Packages</p>
          <div className="flex flex-wrap gap-2">
            {(account.packages || []).map((p) => (
              <span key={p} className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ border: `1px solid ${accent}`, color: accent }}>
                <Sparkles className="h-3 w-3 inline mr-1" />{p}
              </span>
            ))}
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="demo-share-amenities">
          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>Amenities Serviced by NECS</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {(account.amenities || []).map((a) => (
              <div key={a.name} className="flex items-center justify-between gap-2 rounded px-2.5 py-1.5" style={{ backgroundColor: '#0f0f0f', border: `1px solid ${accent}55` }}>
                <span className="text-xs text-white truncate">{a.name}</span>
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ color: accent, border: `1px solid ${accent}` }}>×{a.quantity}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="demo-share-violations">
            <p className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <AlertTriangle className="h-3.5 w-3.5" /> Violations
            </p>
            <div className="space-y-2">
              {(board.violations || []).map((v) => (
                <div key={v.violation_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{(v.violation_type || '').replace(/_/g, ' ')} · {v.area}</p>
                    <p className="text-[10px] text-zinc-500">severity {v.severity}</p>
                  </div>
                  <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ color: v.status === 'resolved' ? '#34d399' : '#fbbf24', border: `1px solid ${v.status === 'resolved' ? '#34d399' : '#fbbf24'}` }}>
                    {v.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="demo-share-routes">
            <p className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <RouteIcon className="h-3.5 w-3.5" /> Routes
            </p>
            <div className="space-y-2">
              {(board.routes || []).map((r) => (
                <div key={r.route_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{r.route_name}</p>
                    <p className="text-[10px] text-zinc-500">{r.stops} stops · crew {r.crew_leader}</p>
                  </div>
                  <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: accent, border: `1px solid ${accent}` }}>{(r.status || '').replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="bg-zinc-900 border-zinc-700 p-5 lg:col-span-2" data-testid="demo-share-tasks">
            <p className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <ClipboardList className="h-3.5 w-3.5" /> Tasks & Assignments
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {(board.tasks || []).map((t) => (
                <div key={t.task_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <p className="font-semibold text-white truncate">{t.title}</p>
                  <span className="text-[10px] text-zinc-400 flex-shrink-0">{t.status}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <p className="text-center text-[11px] text-zinc-500">
          Powered by NECS Technology · Interested? Contact Noble Empire Cleaning Services to activate your live account.
        </p>
      </div>
    </div>
  );
}
